import qs from "qs"
import { Fragment, useState, useEffect, useCallback } from "react"
import { ChevronRightIcon, ChevronDownIcon, LoaderCircleIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react"
import { PopoverClose } from "@radix-ui/react-popover"
import {
  Outlet,
  useFetcher,
  useRouteLoaderData,
  useSearchParams,
  useNavigate,
  useLocation,
  type FetcherWithComponents,
} from "react-router"
import { getOrganisations } from "@/utils/projects"
import { fetchAPI, handleResponse, cn } from "@/utils"
import { hasPermission } from "@/machines/Authmachine"
import { FiltersWrapper, type StoredSession, type SearchCriteria } from "@/components/Filters"
import type { RootLoader } from "../root"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import Pagination from "@/components/ui/pagination"

const routeId = "checks-routeId"
const sessionKey = "checks-filter"

type Criteria = "Contains" | "StartsWith" | "EndsWith" | "Exact"

function normalizeCriteria(value: string | undefined): Criteria {
  switch (value as SearchCriteria | undefined) {
    case "Starts With":
      return "StartsWith"
    case "Ends With":
      return "EndsWith"
    case "Contains":
      return "Contains"
    case "Exact":
      return "Exact"
    default:
      return "StartsWith"
  }
}

export interface Check {
  reference: string
  organisation: { name: string; active: boolean }
  question: { organisation: string; name: string; value: string; code: string; active: boolean }
  guidance: { organisation: string; name: string; code: string; value: string; active: boolean }
  project: { organisation: string; name: string; active: boolean }
  product: string | null
  level: number | string
  order: number | string
  group: string
  outcomeLookup: string
  qualityLookup: string
  ignoreOutcome: boolean
  rootCauses: Array<{ name: string | null; value: string | null }>
  checks: Check[] | null
}

interface ChecksAPIResponse {
  items: Check[]
  page: number | string
  totalCount: number | string
}

interface DeleteActionResponse {
  id: string
  errorMessage?: string
  errorReference?: string
  successMessage?: string
}

function actionResult(response: Omit<DeleteActionResponse, "id">): DeleteActionResponse {
  return { id: crypto.randomUUID(), ...response }
}

const filterFields = [
  { name: "Project", label: "Project" },
  { name: "Reference", label: "Reference" },
  { name: "QuestionName", label: "Question Name" },
  { name: "QuestionText", label: "Question Text" },
  { name: "Code", label: "Code" },
  { name: "Product", label: "Product" },
] as const

async function loader({ request }: { request: Request }) {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const url = new URL(request.url)
  const page = +(url.searchParams.get("page") ?? 1)
  const pageSize = +(url.searchParams.get("pageSize") ?? 10)
  const sessionData = sessionStorage.getItem(sessionKey)

  const filters =
    sessionData !== null && sessionData.length > 0 ? (qs.parse(sessionData) as unknown as Partial<StoredSession>) : {}

  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  const query = new URLSearchParams()
  for (const { name } of filterFields) {
    const value = filters.raw?.[name]
    const criteria = normalizeCriteria(filters.raw?.[`${name}Criteria`])
    query.set(name, value ?? "")
    query.set(`${name}Criteria`, criteria)
  }

  try {
    const response = await fetchAPI<ChecksAPIResponse>(
      `/api/unityReview/Checks/${loadedOrganisation}/${page}/${pageSize}?${query.toString()}`,
    ).then(handleResponse)

    return {
      checks: response.items,
      loadedOrganisation,
      totalCount: Number(response.totalCount),
      page: Number(response.page),
    }
  } catch (error) {
    if (error !== null && typeof error === "object" && "status" in error) {
      const status = (error as { status: number }).status

      if (status === 404) {
        return { checks: [], loadedOrganisation, totalCount: 0, page: 1 }
      }
    }
    throw error
  }
}

async function mainAction({ request }: { request: Request }): Promise<DeleteActionResponse> {
  const formData = await request.formData()
  const action = formData.get("action") as string | null
  const organisation = formData.get("organisation") as string | null

  if (organisation === null || organisation.length === 0) {
    return actionResult({ errorMessage: "Missing organisation" })
  }

  switch (action) {
    case "DELETE": {
      const reference = formData.get("reference") as string | null

      if (reference === null || reference.length === 0) {
        return actionResult({ errorMessage: "Missing check reference" })
      }

      let errorMessage: string | undefined
      try {
        const response = await fetchAPI(
          `/api/unityReview/Checks/${encodeURIComponent(organisation)}/${encodeURIComponent(reference)}`,
          {
            method: "DELETE",
          },
        )

        if (!response.ok) {
          const body: unknown = await response.json().catch(() => null)
          if (Array.isArray(body)) {
            const messages = (body as unknown[])
              .filter(
                (item): item is { errorMessage: string } =>
                  typeof item === "object" &&
                  item !== null &&
                  "errorMessage" in item &&
                  typeof item.errorMessage === "string",
              )
              .map((item) => item.errorMessage)
              .filter((message) => message.length > 0)
            if (messages.length > 0) errorMessage = messages.join(" ")
          } else if (typeof body === "object" && body !== null && "title" in body && typeof body.title === "string") {
            errorMessage = body.title
          }

          errorMessage ??= "Error deleting check"
        }
      } catch {
        errorMessage = "Error deleting check"
      }
      return errorMessage == null
        ? actionResult({ successMessage: `Check ${reference} deleted successfully.` })
        : actionResult({ errorMessage, errorReference: reference })
    }
    default:
      throw new Error("Unknown action")
  }
}

function countSubChecks(check: Check): number {
  return (check.checks ?? []).reduce((total, child) => total + 1 + countSubChecks(child), 0)
}

function SubCheckDeletionNotice({ check }: { check: Check }) {
  const subCheckCount = countSubChecks(check)
  if (subCheckCount === 0) return null

  return (
    <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
      This check has {subCheckCount} sub-check{subCheckCount === 1 ? "" : "s"}. They will be deleted along with it.
    </p>
  )
}

function ActionNotice({ type, message }: { type: "error" | "success"; message: string }) {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const timeout = window.setTimeout(() => setIsVisible(false), 5000)
    return () => window.clearTimeout(timeout)
  }, [])

  if (!isVisible) return null

  return (
    <div
      role={type === "error" ? "alert" : "status"}
      aria-live="polite"
      className={cn(
        "mb-4 rounded-lg border p-4 text-sm",
        type === "error" ? "border-red-200 bg-red-50 text-red-900" : "border-green-200 bg-green-50 text-green-700",
      )}
    >
      {message}
    </div>
  )
}

function DeleteCheckPopover({
  check,
  organisation,
  fetcher,
}: {
  check: Check
  organisation: string
  fetcher: FetcherWithComponents<DeleteActionResponse>
}) {
  const isDeletingThis = fetcher.state !== "idle" && fetcher.formData?.get("reference") === check.reference

  return (
    <Popover>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-red-600 hover:text-red-700 disabled:cursor-not-allowed"
              disabled={fetcher.state !== "idle"}
              aria-label={`Delete check ${check.reference}`}
            >
              <Trash2Icon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>Delete Check</TooltipContent>
      </Tooltip>
      <PopoverContent side="left" align="start" className="max-w-sm">
        <p className="text-sm text-pretty">
          Check &quot;<span className="font-semibold break-all">{check.reference}</span>&quot;
          {check.question?.name != null && check.question.name.length > 0 ? (
            <>
              {" "}
              (question: <span className="font-semibold">{check.question.name}</span>)
            </>
          ) : null}{" "}
          will be permanently deleted. Are you sure?
        </p>
        <SubCheckDeletionNotice check={check} />
        {fetcher.data?.errorReference === check.reference && fetcher.data.errorMessage != null && (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {fetcher.data.errorMessage}
          </p>
        )}
        <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
          <input type="hidden" name="action" value="DELETE" />
          <input type="hidden" name="organisation" value={organisation} />
          <input type="hidden" name="reference" value={check.reference} />
          {isDeletingThis && (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
            </span>
          )}
          <div className="flex-1" />
          <PopoverClose asChild>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </PopoverClose>
          <Button type="submit" variant="destructive" disabled={isDeletingThis}>
            Confirm
          </Button>
        </fetcher.Form>
      </PopoverContent>
    </Popover>
  )
}

function CheckRow({
  check,
  depth = 0,
  organisation,
  canWrite,
  fetcher,
}: {
  check: Check
  depth?: number
  organisation: string
  canWrite: boolean
  fetcher: FetcherWithComponents<DeleteActionResponse>
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()
  const hasSubChecks = (check.checks?.length ?? 0) > 0
  const isChildRow = depth > 0

  return (
    <Fragment>
      <TableRow className={cn("text-sm", isChildRow && "bg-muted/40 hover:bg-muted/60")}>
        <TableCell className="relative" style={{ paddingLeft: `${depth * 1.5 + 0.5}rem` }}>
          {hasSubChecks ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 cursor-pointer"
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-label={isExpanded ? "Collapse sub-checks" : "Expand sub-checks"}
            >
              {isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
            </Button>
          ) : (
            <span className="inline-block w-6" />
          )}
        </TableCell>
        <TableCell className={cn("py-3 break-all whitespace-normal", isChildRow && "text-muted-foreground")}>
          {depth === 0 ? (
            check.reference
          ) : (
            <span className="flex items-start gap-1.5">
              <span className="text-border">└</span>
              {check.reference}
            </span>
          )}
        </TableCell>
        <TableCell className="wrap-break-word whitespace-normal">
          <span className="flex flex-col gap-0.5">
            <span>Name: {check.question?.name}</span>
            {check.question?.code != null && check.question.code.length > 0 && (
              <span className="text-xs text-muted-foreground">Code: {check.question.code}</span>
            )}
          </span>
        </TableCell>
        <TableCell className="overflow-hidden">
          {check.question?.value != null && check.question.value.length > 0 ? (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="block w-full cursor-pointer truncate text-left decoration-dotted underline-offset-2 hover:underline"
                >
                  {check.question.value}
                </button>
              </PopoverTrigger>
              <PopoverContent className="max-w-sm text-sm whitespace-pre-wrap">{check.question.value}</PopoverContent>
            </Popover>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="wrap-break-word whitespace-normal">{check.project?.name}</TableCell>
        <TableCell className="wrap-break-word whitespace-normal">{check.product}</TableCell>
        <TableCell>{check.level}</TableCell>
        <TableCell>{check.order}</TableCell>
        <TableCell className="wrap-break-word whitespace-normal">{check.group}</TableCell>
        <TableCell>
          {hasSubChecks && (
            <span className="text-xs text-muted-foreground">
              {check.checks!.length} sub-check{check.checks!.length === 1 ? "" : "s"}
            </span>
          )}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1">
            {depth === 0 && canWrite && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() =>
                      void navigate(
                        `${encodeURIComponent(check.reference)}/edit?organisation=${encodeURIComponent(organisation)}`,
                      )
                    }
                    aria-label="Edit check"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit Check</TooltipContent>
              </Tooltip>
            )}
            {depth === 0 && canWrite && (
              <DeleteCheckPopover check={check} organisation={organisation} fetcher={fetcher} />
            )}
          </div>
        </TableCell>
      </TableRow>
      {isExpanded &&
        check.checks?.map((subCheck) => (
          <CheckRow
            key={subCheck.reference}
            check={subCheck}
            depth={depth + 1}
            organisation={organisation}
            canWrite={canWrite}
            fetcher={fetcher}
          />
        ))}
    </Fragment>
  )
}

function Main() {
  const fetcher = useFetcher<DeleteActionResponse>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null) {
    return <div className="grid h-screen place-items-center">Loading...</div>
  }

  const canWrite = hasPermission("writeAccess", {
    organisation: data?.loadedOrganisation,
  })

  const noticeMessage =
    fetcher.data?.successMessage ?? (fetcher.data?.errorReference == null ? fetcher.data?.errorMessage : undefined)

  return (
    <div className="h-screen overflow-x-hidden overflow-y-auto">
      <div className="sticky top-0 z-20 bg-background">
        <Pagination routeId={routeId} />
        {fetcher.data != null && noticeMessage != null && (
          <ActionNotice
            key={fetcher.data.id}
            type={fetcher.data.successMessage != null ? "success" : "error"}
            message={noticeMessage}
          />
        )}
      </div>

      <div className="w-full overflow-x-auto">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead className="w-28 text-left whitespace-normal">Reference</TableHead>
              <TableHead className="w-40 text-left whitespace-normal">Question</TableHead>
              <TableHead className="w-1/4 text-left whitespace-normal">Value</TableHead>
              <TableHead className="w-32 text-left whitespace-normal">Project</TableHead>
              <TableHead className="w-24 text-left whitespace-normal">Product</TableHead>
              <TableHead className="w-16 text-left">Level</TableHead>
              <TableHead className="w-16 text-left">Order</TableHead>
              <TableHead className="w-24 text-left whitespace-normal">Group</TableHead>
              <TableHead className="w-24 text-left">Sub-checks</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody key={data.page}>
            {data.checks.length > 0 ? (
              data.checks.map((check) => (
                <CheckRow
                  key={check.reference}
                  check={check}
                  organisation={data.loadedOrganisation}
                  canWrite={canWrite}
                  fetcher={fetcher}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={11} className="py-4 text-center text-gray-500">
                  No checks found for this organisation.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Outlet context={{ loadedOrganisation: data.loadedOrganisation, checkItems: data.checks }} />
    </div>
  )
}

export function Checks() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const canWrite = hasPermission("writeAccess", {
    organisation: data?.loadedOrganisation,
  })

  const isFormPage = location.pathname.includes("/create") || location.pathname.includes("/edit")

  const organisations = rootData?.organisations ?? []
  const hasOrganisations = organisations.length > 0
  const loadedOrg = data?.loadedOrganisation
  const defaultOrganisation =
    loadedOrg != null && loadedOrg.length > 0 ? loadedOrg : hasOrganisations ? organisations[0] : ""

  const resetPage = useCallback(() => {
    setSearchParams((params: URLSearchParams) => {
      params.delete("page")
      return params
    })
  }, [setSearchParams])

  return (
    <div className="flex w-full flex-col gap-4 overflow-x-hidden">
      {!isFormPage && (
        <div className="w-full">
          <div className="flex w-full items-end justify-between gap-4">
            <div className="flex flex-1 flex-wrap items-end gap-4">
              {hasOrganisations && (
                <FiltersWrapper
                  submitOnChange
                  canReset
                  sessionKey={sessionKey}
                  onAfterSubmit={resetPage}
                  onReset={resetPage}
                  className="w-full min-w-0"
                >
                  <FiltersWrapper.Select
                    name="organisation"
                    label="Organisation"
                    defaultValue={defaultOrganisation}
                    items={organisations}
                  />

                  {filterFields.map((field) => (
                    <FiltersWrapper.Input key={field.name} name={field.name} label={field.label} />
                  ))}
                </FiltersWrapper>
              )}
            </div>
            {canWrite && (
              <Button
                type="button"
                onClick={() => void navigate(`create?organisation=${encodeURIComponent(defaultOrganisation)}`)}
                className="shrink-0"
                variant="outline"
              >
                <PlusIcon className="mr-1 h-4 w-4" />
                Add New
              </Button>
            )}
          </div>
        </div>
      )}

      <Outlet
        context={{
          loadedOrganisation: data?.loadedOrganisation,
          checkItems: data?.checks ?? [],
        }}
      />
    </div>
  )
}

Checks.Main = Main
Checks.loader = loader
Checks.routeId = routeId

Main.action = mainAction
